/**
 * Homepage Ask Scan AI — Alpine orchestrator for the homepage hero tabs.
 * Book a scan keeps the booking bar. Help me choose inlines the chat chrome.
 * Send / quota / recaptcha / session live in carved units.
 */

import { registerAiChatLoader } from "./aiChatLoader";
import { registerAiChatMessage } from "./aiChatMessage";
import {
  AI_TRIAGE_RECOMMENDATION_EVENT,
  AI_TRIAGE_SUGGEST_EVENT,
  AI_TRIAGE_SUGGESTIONS_EVENT,
  latestFollowUpSuggestionLabels,
  postSuggestionChipPress,
  readOpeningChips,
  retireStaleSuggestionChips,
  trayChipLabels,
  visibleSuggestionLabels,
} from "./suggestionChips";
import {
  AI_TRIAGE_BLOCK_REASONS,
  inferBlockReasonFromNotice,
  trackAiTriageBlocked,
  trackAiTriageChatCreated,
  trackAiTriageClosed,
  trackAiTriageFeedback,
  trackAiTriageGuardrailBlocked,
  trackAiTriageMessageSent,
  trackAiTriageOpened,
  trackAiTriageRecommendationShown,
  trackAiTriageStarterChipsShown,
  trackAiTriageSuggestionClicked,
} from "./aiTriageTracking";
import {
  FEEDBACK_REVEAL_DELAY_MS,
  FEEDBACK_RATING_DISMISSED,
  FEEDBACK_STATE_HIDDEN,
  FEEDBACK_STATE_PROMPT,
  FEEDBACK_STATE_THANKS,
  FEEDBACK_THANKS_VISIBLE_MS,
  shouldRevealFeedbackPrompt,
  writeStoredFeedback,
} from "./aiTriageFeedback";
import {
  fetchRecaptchaToken,
  recaptchaSiteKey,
  setRecaptchaBadgeVisible,
} from "./aiTriageRecaptcha";
import {
  MESSAGE_QUOTA_MAX_FALLBACK,
  MESSAGE_QUOTA_NEAR_LIMIT,
  PAUSE_REASON_CHAT_CREATE,
  PAUSE_REASON_DAILY,
  PAUSE_REASON_HOURLY,
  composerBlockHoldSeconds,
  composerLockHelper,
  isHourlyPause,
  isQuotaExhausted,
  messageQuotaLabel,
  messageQuotaNudge,
  shouldShowMessageQuota,
  pauseReasonFromError,
  quotaPauseFromPayload,
  shouldHoldComposerBlockWithoutRetry,
} from "./aiTriageQuota";
import {
  consumeAskScanQuery,
  readStoredResumeToken,
  writeStoredResumeToken,
} from "./aiTriageSession";
import {
  COMPOSER_MAX_HEIGHT_PX,
  OPENING_COMPOSER_PLACEHOLDER,
  REPLY_COMPOSER_PLACEHOLDER,
  STREAM_REPLY_TIMEOUT_MS,
  STREAMING_MARKER_ATTR,
  jsonHeaders,
  lastRowIsSettledAssistant,
  resizeComposerField,
  syncReplyInFlight,
} from "./aiTriageComposer";
import { createTranscriptFollowScroll } from "./aiTriageTranscriptScroll";
import { scrollWindowToOpenHeroPanel } from "./aiTriagePanelOpenScroll";
import { SPECIALIST_CONSULTATIONS_OPENED_EVENT } from "./SpecialistConsultations/events";

const MODE_SEARCH = "search";
const MODE_CHAT = "chat";
const MODE_SPECIALIST = "specialist";
const BOOTSTRAP_STATE_IDLE = "idle";
const BOOTSTRAP_STATE_LOADING = "loading";
const BOOTSTRAP_STATE_READY = "ready";
const BOOTSTRAP_STATE_ERROR = "error";
const BOOTSTRAP_ERROR_VERIFY =
  "We couldn't verify this request. Please try again.";
const BOOTSTRAP_ERROR_GENERIC = "We couldn't start the chat. Please try again.";
const COMPOSER_BLOCK_TICK_MS = 1000;
const MILLISECONDS_PER_SECOND = 1000;
const OPEN_SOURCE_MODE = "mode";
const OPEN_SOURCE_REOPEN = "reopen";
const MESSAGE_VIA_TYPED = "typed";
const MESSAGE_VIA_SUGGESTION = "suggestion";
const TRACK_STAGE_CREATE = "create";
const TRACK_STAGE_COMPOSER = "composer";
const GUARDRAIL_EMERGENCY = "emergency";
const COMPOSER_FIELD_SELECTOR = "#ai-triage-message-content";
const MESSAGES_ID = "messages";
const ASSISTANT_PENDING_ID = "assistant-pending";
const LOADER_TEMPLATE_ID = "ai-chat-loader-template";
const CARD_HOST_ID = "ai-triage-card-host";
const OPEN_TRIGGER_ID = "ai-triage-open-trigger";
const STREAM_STATUS_ID = "ai-triage-stream-status";
const COMPOSER_WAIT_ANNOUNCEMENT = "Scan AI is replying";
const COMPOSER_FOCUS_OPTIONS = { preventScroll: true };
const RATE_LIMIT_FALLBACK =
  "You're sending messages a bit quickly — please wait a moment and try again.";
const ELEMENT_NODE = 1;
const STREAM_SUBSCRIBE_TIMEOUT_MS = 5000;
const ALPINE_DATA_STACK = "_x_dataStack";

export function initAiChatMessageNode(node, alpine = window.Alpine) {
  if (!node || node.nodeType !== ELEMENT_NODE) {
    return false;
  }
  if (typeof node.hasAttribute !== "function" || !node.hasAttribute("x-data")) {
    return false;
  }
  if (Object.prototype.hasOwnProperty.call(node, ALPINE_DATA_STACK)) {
    return false;
  }
  if (!alpine || typeof alpine.initTree !== "function") {
    return false;
  }
  alpine.initTree(node);
  return true;
}

function initAddedAiChatMessageNodes(node) {
  if (!node || node.nodeType !== ELEMENT_NODE) {
    return;
  }
  if (typeof node.hasAttribute === "function" && node.hasAttribute("x-data")) {
    initAiChatMessageNode(node);
  }
  node.querySelectorAll?.("[x-data]").forEach((element) => {
    initAiChatMessageNode(element);
  });
}

function csrfToken() {
  return (
    document
      .querySelector("meta[name='csrf-token']")
      ?.getAttribute("content") || ""
  );
}

function composerField(root = document) {
  return root.querySelector(COMPOSER_FIELD_SELECTOR);
}

function restoreComposerText(text, via) {
  const field = composerField();
  if (via !== "composer" || !field || field.value.trim()) {
    return;
  }
  field.value = text;
  resizeComposerField(field, COMPOSER_MAX_HEIGHT_PX);
}

export function waitForStreamSubscription(
  streamSource,
  timeoutMs = STREAM_SUBSCRIBE_TIMEOUT_MS
) {
  return new Promise((resolve) => {
    if (streamSource.hasAttribute("connected")) {
      resolve(true);
      return;
    }
    const observer = new MutationObserver(() => {
      if (streamSource.hasAttribute("connected")) {
        finish(true);
      }
    });
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    function finish(connected) {
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(connected);
    }
    observer.observe(streamSource, {
      attributes: true,
      attributeFilter: ["connected"],
    });
  });
}

function announceComposerWait() {
  const status = document.getElementById(STREAM_STATUS_ID);
  if (!status) {
    return;
  }
  status.textContent = "";
  status.textContent = COMPOSER_WAIT_ANNOUNCEMENT;
}

const transcriptFollow = createTranscriptFollowScroll();

function scheduleTranscriptFollow() {
  transcriptFollow.followLiveIfPinned();
}

function countUserMessages() {
  return document.querySelectorAll("#messages .ai-chat-message").length;
}

function appendUserBubble(text) {
  const log = document.getElementById(MESSAGES_ID);
  if (!log) {
    return;
  }
  const row = document.createElement("div");
  row.className = "ai-chat-message flex flex-row-reverse items-end gap-2.5";
  const col = document.createElement("div");
  col.className = "flex min-w-0 max-w-[78%] flex-col items-end gap-1.5";
  const bubble = document.createElement("div");
  bubble.className =
    "min-w-0 rounded-2xl rounded-br-md bg-green-primary px-4 py-2.5 text-white";
  const body = document.createElement("div");
  body.className = "whitespace-pre-wrap break-words text-sm leading-relaxed";
  body.textContent = text;
  bubble.appendChild(body);
  col.appendChild(bubble);
  row.appendChild(col);
  log.appendChild(row);
}

export function aiTriageHome() {
  return {
    mode: MODE_SEARCH,
    chatId: null,
    resumeToken: null,
    hasMessages: false,
    chatBootstrapError: null,
    chatBootstrapState: BOOTSTRAP_STATE_IDLE,
    composerBlocked: false,
    composerBlockedMessage: null,
    emergencyHelpContactVisible: false,
    composerBlockReason: PAUSE_REASON_HOURLY,
    composerBlockSecondsLeft: 0,
    replyInFlight: false,
    _awaitingAssistantReply: false,
    remainingMessages: null,
    maxMessages: MESSAGE_QUOTA_MAX_FALLBACK,
    _streamSource: null,
    _restorePromise: null,
    _chatReadyPromise: null,
    _streamReady: null,
    _sendPending: false,
    _resetGeneration: 0,
    _composerBlockTimer: null,
    _messagesObserver: null,
    _unbindTranscriptFollow: null,
    _openTrigger: null,
    followUpSuggestionLabels: [],
    openingChips: [],
    _starterChipsShownTracked: false,
    chatOpenedAtMs: null,
    _trackedRecommendationKeys: null,
    _trackedGuardrailIds: null,
    feedbackState: FEEDBACK_STATE_HIDDEN,
    _feedbackRevealTimer: null,
    _feedbackThanksTimer: null,
    _streamReplyTimer: null,

    get isChatMode() {
      return this.mode === MODE_CHAT;
    },

    get isSearchMode() {
      return this.mode === MODE_SEARCH;
    },

    get isSpecialistMode() {
      return this.mode === MODE_SPECIALIST;
    },

    get isBootstrapping() {
      return this.chatBootstrapState === BOOTSTRAP_STATE_LOADING;
    },

    get hasBootstrapError() {
      return this.chatBootstrapState === BOOTSTRAP_STATE_ERROR;
    },

    get composerLocked() {
      return (
        this.isBootstrapping ||
        this.composerPaused ||
        this.replyInFlight ||
        this._sendPending
      );
    },

    get composerPaused() {
      return this.composerBlocked || this.quotaExhausted;
    },

    get quotaExhausted() {
      return isQuotaExhausted(this.remainingMessages);
    },

    get composerLockHelper() {
      if (!this.composerPaused) {
        return "";
      }
      return composerLockHelper({
        reason: this.composerBlockReason,
        secondsLeft: this.composerBlockSecondsLeft,
      });
    },

    get showMessageQuota() {
      return (
        !this.composerPaused && shouldShowMessageQuota(this.remainingMessages)
      );
    },

    get messageQuotaNearLimit() {
      return (
        this.showMessageQuota &&
        this.remainingMessages <= MESSAGE_QUOTA_NEAR_LIMIT
      );
    },

    get messageQuotaLabel() {
      return this.showMessageQuota
        ? messageQuotaLabel(this.remainingMessages)
        : "";
    },

    get messageQuotaNudge() {
      return this.showMessageQuota
        ? messageQuotaNudge(this.remainingMessages)
        : "";
    },

    get composerPlaceholder() {
      return this.hasMessages
        ? REPLY_COMPOSER_PLACEHOLDER
        : OPENING_COMPOSER_PLACEHOLDER;
    },

    get visibleChipLabels() {
      return trayChipLabels({
        hasMessages: this.hasMessages,
        replyInFlight: this.replyInFlight,
        followUpLabels: this.followUpSuggestionLabels,
        openingLabels: this.openingChips.map((chip) => chip.label),
      });
    },

    get showFeedbackPrompt() {
      return this.feedbackState === FEEDBACK_STATE_PROMPT;
    },

    get showFeedbackThanks() {
      return this.feedbackState === FEEDBACK_STATE_THANKS;
    },

    init() {
      this._trackedRecommendationKeys = new Set();
      this._trackedGuardrailIds = new Set();
      this._openTrigger = document.getElementById(OPEN_TRIGGER_ID);
      this.openingChips = readOpeningChips();
      this.observeMessages();
      this.bindTriageEvents();
      this._restorePromise = this.restoreExistingChat();
    },

    destroy() {
      if (this._feedbackRevealTimer) {
        window.clearTimeout(this._feedbackRevealTimer);
      }
      if (this._feedbackThanksTimer) {
        window.clearTimeout(this._feedbackThanksTimer);
      }
      this.clearStreamReplyTimeout();
      this._unbindTranscriptFollow?.();
      this._messagesObserver?.disconnect();
      window.removeEventListener(AI_TRIAGE_SUGGEST_EVENT, this._onSuggest);
      window.removeEventListener(
        AI_TRIAGE_SUGGESTIONS_EVENT,
        this._onSuggestionLabels
      );
      window.removeEventListener(
        AI_TRIAGE_RECOMMENDATION_EVENT,
        this._onRecommendation
      );
      this.disconnectStream();
      setRecaptchaBadgeVisible(false);
    },

    observeMessages() {
      const log = document.getElementById(MESSAGES_ID);
      if (!log) {
        return;
      }
      this.syncMessageState();
      this._unbindTranscriptFollow = transcriptFollow.bind();
      this._messagesObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            initAddedAiChatMessageNodes(node);
          });
        });
        this.syncMessageState();
        scheduleTranscriptFollow();
      });
      this._messagesObserver.observe(log, { childList: true, subtree: true });
    },

    syncMessageState() {
      const log = document.getElementById(MESSAGES_ID);
      const streamingInDom = Boolean(
        log?.querySelector(`[${STREAMING_MARKER_ATTR}]`)
      );
      const settledAssistantArrived = lastRowIsSettledAssistant(log);
      if (streamingInDom || settledAssistantArrived) {
        document.getElementById(ASSISTANT_PENDING_ID)?.remove();
      }
      this.hasMessages = Boolean(log?.children?.length);
      const next = syncReplyInFlight({
        streamingInDom,
        awaitingAssistantReply: this._awaitingAssistantReply,
        settledAssistantArrived:
          this._awaitingAssistantReply && settledAssistantArrived,
      });
      this.replyInFlight = next.replyInFlight;
      this._awaitingAssistantReply = next.awaitingAssistantReply;
      if (!this.replyInFlight) {
        this.clearStreamReplyTimeout();
      }
      const host = document.getElementById(CARD_HOST_ID);
      if (host) {
        host.dataset.replyInFlight = this.replyInFlight.toString();
      }
      retireStaleSuggestionChips();
      this.syncGuardrailTrackingFromDom();
      if (this.replyInFlight) {
        announceComposerWait();
      } else if (this.hasMessages) {
        this.followUpSuggestionLabels = latestFollowUpSuggestionLabels();
      }
    },

    bindTriageEvents() {
      this._onSuggest = (event) => {
        const text = event.detail?.text;
        if (text) {
          this.sendSuggestion(text);
        }
      };
      this._onSuggestionLabels = (event) => {
        this.followUpSuggestionLabels = visibleSuggestionLabels(
          event.detail?.labels || []
        );
      };
      this._onRecommendation = (event) => {
        this.trackRecommendationShown(event.detail || {});
        this.revealFeedbackPrompt();
      };
      window.addEventListener(AI_TRIAGE_SUGGEST_EVENT, this._onSuggest);
      window.addEventListener(
        AI_TRIAGE_SUGGESTIONS_EVENT,
        this._onSuggestionLabels
      );
      window.addEventListener(
        AI_TRIAGE_RECOMMENDATION_EVENT,
        this._onRecommendation
      );
    },

    revealFeedbackPrompt() {
      if (
        !shouldRevealFeedbackPrompt({
          feedbackState: this.feedbackState,
          chatId: this.chatId,
        })
      ) {
        return;
      }
      if (this._feedbackRevealTimer) {
        return;
      }

      this._feedbackRevealTimer = window.setTimeout(() => {
        this._feedbackRevealTimer = null;
        if (
          !shouldRevealFeedbackPrompt({
            feedbackState: this.feedbackState,
            chatId: this.chatId,
          })
        ) {
          return;
        }
        this.feedbackState = FEEDBACK_STATE_PROMPT;
      }, FEEDBACK_REVEAL_DELAY_MS);
    },

    dismissFeedback() {
      if (this._feedbackRevealTimer) {
        window.clearTimeout(this._feedbackRevealTimer);
        this._feedbackRevealTimer = null;
      }
      writeStoredFeedback(this.chatId, FEEDBACK_RATING_DISMISSED);
      trackAiTriageFeedback({ rating: FEEDBACK_RATING_DISMISSED });
      this.feedbackState = FEEDBACK_STATE_HIDDEN;
    },

    async submitFeedback(rating) {
      if (this.feedbackState !== FEEDBACK_STATE_PROMPT) {
        return;
      }

      writeStoredFeedback(this.chatId, rating);
      trackAiTriageFeedback({ rating });
      this.feedbackState = FEEDBACK_STATE_THANKS;

      if (this.chatId) {
        try {
          await fetch(`/chats/${this.chatId}/feedback`, {
            method: "POST",
            headers: jsonHeaders(csrfToken()),
            credentials: "same-origin",
            body: JSON.stringify({ rating }),
          });
        } catch (_error) {
          // Local + Heap capture is enough if the network request fails.
        }
      }

      if (this._feedbackThanksTimer) {
        window.clearTimeout(this._feedbackThanksTimer);
      }
      this._feedbackThanksTimer = window.setTimeout(() => {
        this.feedbackState = FEEDBACK_STATE_HIDDEN;
        this._feedbackThanksTimer = null;
      }, FEEDBACK_THANKS_VISIBLE_MS);
    },

    trackRecommendationShown(detail = {}) {
      const key = detail.productId || "unknown";
      if (this._trackedRecommendationKeys.has(key)) {
        return;
      }
      const isFirstRecommendation = this._trackedRecommendationKeys.size === 0;
      this._trackedRecommendationKeys.add(key);
      trackAiTriageRecommendationShown({
        productId: detail.productId,
        messageIndex: countUserMessages(),
        isFirstRecommendation,
      });
    },

    syncGuardrailTrackingFromDom() {
      const emergencyNodes = document.querySelectorAll(
        `#${MESSAGES_ID} [data-ai-triage-guardrail="${GUARDRAIL_EMERGENCY}"]`
      );
      if (emergencyNodes.length > 0) {
        this.emergencyHelpContactVisible = true;
      }
      emergencyNodes.forEach((element) => {
        const id = element.id || element.dataset.aiTriageGuardrail;
        if (!id || this._trackedGuardrailIds.has(id)) {
          return;
        }
        this._trackedGuardrailIds.add(id);
        trackAiTriageGuardrailBlocked();
      });
    },

    openChat() {
      const reopen = Boolean(this.chatId) || this.hasMessages;
      this.chatOpenedAtMs = Date.now();
      trackAiTriageOpened({
        source: reopen ? OPEN_SOURCE_REOPEN : OPEN_SOURCE_MODE,
      });
      this.mode = MODE_CHAT;
      // A restored chat has no starter tray, so wait for restore before counting impressions.
      Promise.resolve(this._restorePromise)
        .catch(() => {})
        .then(() => this.trackStarterChipsShownOnce());
      this._openTrigger = document.getElementById(OPEN_TRIGGER_ID);
      setRecaptchaBadgeVisible(true);
      this.$nextTick(() => {
        composerField()?.focus(COMPOSER_FOCUS_OPTIONS);
        scheduleTranscriptFollow();
        scrollWindowToOpenHeroPanel();
      });
    },

    trackStarterChipsShownOnce() {
      if (this.hasMessages || this._starterChipsShownTracked) {
        return;
      }
      this._starterChipsShownTracked = true;
      trackAiTriageStarterChipsShown(this.openingChips.map((chip) => chip.key));
    },

    closeChat() {
      const durationSeconds =
        this.chatOpenedAtMs == null
          ? undefined
          : Math.max(
              0,
              Math.round(
                (Date.now() - this.chatOpenedAtMs) / MILLISECONDS_PER_SECOND
              )
            );
      trackAiTriageClosed({
        hasRecommendation: Boolean(
          document.querySelector(`#${MESSAGES_ID} .ai-triage-recommendation`)
        ),
        durationSeconds,
      });
      this.chatOpenedAtMs = null;
      this.mode = MODE_SEARCH;
      setRecaptchaBadgeVisible(false);
      if (this._openTrigger && typeof this._openTrigger.focus === "function") {
        this._openTrigger.focus(COMPOSER_FOCUS_OPTIONS);
      }
    },

    backToBooking() {
      this.mode = MODE_SEARCH;
      setRecaptchaBadgeVisible(false);
    },

    openSpecialistSearch() {
      if (this.mode === MODE_SPECIALIST) {
        return;
      }
      this.mode = MODE_SPECIALIST;
      setRecaptchaBadgeVisible(false);
      this.$dispatch(SPECIALIST_CONSULTATIONS_OPENED_EVENT, {
        source: "ai_triage_tabs",
      });
    },

    resizeComposerField(field) {
      resizeComposerField(field, COMPOSER_MAX_HEIGHT_PX);
    },

    onComposerEnter(event) {
      if (event.shiftKey) {
        return;
      }
      event.preventDefault();
      event.target.form?.requestSubmit();
    },

    onComposerSubmit(event) {
      event.preventDefault();
      const field = composerField();
      const text = field?.value?.trim();
      if (!text || this.composerLocked) {
        return;
      }
      field.value = "";
      resizeComposerField(field, COMPOSER_MAX_HEIGHT_PX);
      this.sendMessage(text, "composer");
    },

    sendSuggestion(text) {
      if (!text || this.composerLocked) {
        return;
      }
      const position = this.hasMessages
        ? -1
        : this.openingChips.findIndex((chip) => chip.label === text);
      const chipKey = this.openingChips[position]?.key;
      if (chipKey) {
        postSuggestionChipPress(chipKey, { token: csrfToken() });
      }
      trackAiTriageSuggestionClicked({
        via: MESSAGE_VIA_SUGGESTION,
        chipKey,
        chipPosition: chipKey ? position + 1 : undefined,
      });
      this.sendMessage(text, MESSAGE_VIA_SUGGESTION);
    },

    async sendMessage(text, via = "composer") {
      const trimmed = (text || "").toString().trim();
      if (!trimmed) {
        return;
      }

      if (this.replyInFlight || this._sendPending) {
        return;
      }

      this._sendPending = true;
      this.replyInFlight = true;
      this._awaitingAssistantReply = true;
      this.followUpSuggestionLabels = [];
      appendUserBubble(trimmed);
      this.hasMessages = true;
      const log = document.getElementById(MESSAGES_ID);
      const loaderTemplate = document.getElementById(LOADER_TEMPLATE_ID);
      if (
        log &&
        loaderTemplate &&
        !document.getElementById(ASSISTANT_PENDING_ID)
      ) {
        const pending = document.createElement("div");
        pending.id = ASSISTANT_PENDING_ID;
        pending.appendChild(loaderTemplate.content.cloneNode(true));
        log.appendChild(pending);
        initAiChatMessageNode(pending.querySelector("[x-data]"));
      }
      transcriptFollow.pin();
      scheduleTranscriptFollow();

      const sendGeneration = this._resetGeneration;
      const staleSend = () => this._resetGeneration !== sendGeneration;

      let chatId;
      try {
        chatId = await this.ensureAiTriageChat();
        if (staleSend()) {
          return;
        }
        if (chatId) {
          // Replies that skip the model (the emergency notice) are broadcast straight away and would be lost
          // if the message were posted before the stream subscription is confirmed.
          await this._streamReady;
        }
      } finally {
        this._sendPending = false;
      }
      if (staleSend()) {
        return;
      }
      if (!chatId) {
        document.getElementById(ASSISTANT_PENDING_ID)?.remove();
        this.replyInFlight = false;
        this._awaitingAssistantReply = false;
        restoreComposerText(trimmed, via);
        return;
      }

      const messageVia =
        via === MESSAGE_VIA_SUGGESTION
          ? MESSAGE_VIA_SUGGESTION
          : MESSAGE_VIA_TYPED;
      try {
        const response = await fetch(`/chats/${chatId}/messages`, {
          method: "POST",
          headers: jsonHeaders(csrfToken()),
          credentials: "same-origin",
          body: JSON.stringify({
            message: { content: trimmed, via: messageVia },
          }),
        });
        if (staleSend()) {
          return;
        }
        const data = await response.json().catch(() => ({}));
        if (staleSend()) {
          return;
        }

        if (response.status === 429 || data.composer_blocked) {
          document.getElementById(ASSISTANT_PENDING_ID)?.remove();
          this.enterComposerBlock({
            reason: pauseReasonFromError(data.error),
            message: data.message || RATE_LIMIT_FALLBACK,
            retryAfterSeconds: Number(data.retry_after_seconds || 0),
            remainingMessages: data.remaining_messages,
          });
          trackAiTriageBlocked({
            reason:
              inferBlockReasonFromNotice(data.message) ||
              AI_TRIAGE_BLOCK_REASONS.RATE_LIMITED,
            stage: TRACK_STAGE_COMPOSER,
          });
          return;
        }

        if (!response.ok) {
          this.markSendFailure(data.message || BOOTSTRAP_ERROR_GENERIC);
          return;
        }

        this.applyQuotaFromPayload({
          remainingMessages: data.remaining_messages,
          maxMessages: data.max_messages,
          retryAfterSeconds: data.retry_after_seconds,
        });
        this.armStreamReplyTimeout();
        trackAiTriageMessageSent({
          messageIndex: countUserMessages(),
          hasAttachment: false,
          via: messageVia,
        });
      } catch (_error) {
        if (staleSend()) {
          return;
        }
        this.markSendFailure(BOOTSTRAP_ERROR_GENERIC);
      }
    },

    markSendFailure(message) {
      document.getElementById(ASSISTANT_PENDING_ID)?.remove();
      this.clearStreamReplyTimeout();
      this.chatBootstrapState = BOOTSTRAP_STATE_ERROR;
      this.chatBootstrapError = message || BOOTSTRAP_ERROR_GENERIC;
      this._awaitingAssistantReply = false;
      this.replyInFlight = false;
    },

    clearStreamReplyTimeout() {
      if (this._streamReplyTimer) {
        window.clearTimeout(this._streamReplyTimer);
        this._streamReplyTimer = null;
      }
    },

    armStreamReplyTimeout() {
      this.clearStreamReplyTimeout();
      this._streamReplyTimer = window.setTimeout(() => {
        this._streamReplyTimer = null;
        if (!this._awaitingAssistantReply && !this.replyInFlight) {
          return;
        }
        this.markSendFailure(BOOTSTRAP_ERROR_GENERIC);
      }, STREAM_REPLY_TIMEOUT_MS);
    },

    setMessageQuota({ remainingMessages, maxMessages } = {}) {
      if (remainingMessages !== undefined && remainingMessages !== null) {
        this.remainingMessages = Number(remainingMessages);
      }
      if (maxMessages !== undefined && maxMessages !== null) {
        this.maxMessages = Number(maxMessages);
      }
    },

    applyQuotaFromPayload({
      remainingMessages,
      maxMessages,
      retryAfterSeconds,
    } = {}) {
      this.setMessageQuota({ remainingMessages, maxMessages });
      const pause = quotaPauseFromPayload({
        remainingMessages,
        retryAfterSeconds,
      });
      if (pause.exhausted) {
        this.enterComposerBlock({
          reason: PAUSE_REASON_HOURLY,
          retryAfterSeconds: pause.retryAfterSeconds,
          remainingMessages: 0,
        });
        return;
      }
      if (isHourlyPause(this.composerBlockReason)) {
        this.clearComposerBlock();
      }
    },

    clearComposerBlock() {
      this.composerBlocked = false;
      this.composerBlockedMessage = null;
      this.composerBlockSecondsLeft = 0;
      this.composerBlockReason = PAUSE_REASON_HOURLY;
      if (this._composerBlockTimer) {
        window.clearInterval(this._composerBlockTimer);
        this._composerBlockTimer = null;
      }
    },

    unlockComposerAfterWait() {
      this.remainingMessages = null;
      this.clearComposerBlock();
    },

    onComposerWaitElapsed() {
      if (this.composerBlockReason === PAUSE_REASON_CHAT_CREATE) {
        this.clearComposerBlock();
        this.ensureAiTriageChat();
        return;
      }
      this.unlockComposerAfterWait();
    },

    enterComposerBlock({
      message,
      retryAfterSeconds = 0,
      reason = PAUSE_REASON_HOURLY,
      remainingMessages,
    } = {}) {
      const seconds = composerBlockHoldSeconds({
        retryAfterSeconds,
        reason,
      });
      this.composerBlocked = true;
      this.composerBlockReason = reason;
      this.composerBlockedMessage = message || RATE_LIMIT_FALLBACK;
      this.composerBlockSecondsLeft = seconds;
      if (reason === PAUSE_REASON_HOURLY || reason === PAUSE_REASON_DAILY) {
        this.remainingMessages =
          remainingMessages === undefined || remainingMessages === null
            ? 0
            : Number(remainingMessages);
      }
      this.replyInFlight = false;
      this._awaitingAssistantReply = false;
      this.clearStreamReplyTimeout();

      if (this._composerBlockTimer) {
        window.clearInterval(this._composerBlockTimer);
        this._composerBlockTimer = null;
      }
      if (seconds <= 0) {
        if (!shouldHoldComposerBlockWithoutRetry(reason)) {
          this.onComposerWaitElapsed();
        }
        return;
      }

      this._composerBlockTimer = window.setInterval(() => {
        this.composerBlockSecondsLeft = Math.max(
          0,
          this.composerBlockSecondsLeft - 1
        );
        if (this.composerBlockSecondsLeft <= 0) {
          window.clearInterval(this._composerBlockTimer);
          this._composerBlockTimer = null;
          this.onComposerWaitElapsed();
        }
      }, COMPOSER_BLOCK_TICK_MS);
    },

    retryChatBootstrap() {
      this.chatBootstrapState = BOOTSTRAP_STATE_IDLE;
      this.chatBootstrapError = null;
      this._chatReadyPromise = null;
      this.ensureAiTriageChat();
    },

    async startNewChat() {
      if (!this.hasMessages) {
        return;
      }

      this._resetGeneration += 1;
      const createdId = await this.createChat();
      if (!createdId) {
        this.restoreComposerAfterFailedReset();
        return;
      }

      this.resetTranscriptUi();
    },

    restoreComposerAfterFailedReset() {
      this.chatBootstrapState = BOOTSTRAP_STATE_READY;
      this.chatBootstrapError = null;
      if (this.composerBlockReason === PAUSE_REASON_CHAT_CREATE) {
        this.clearComposerBlock();
      }
    },

    resetTranscriptUi() {
      if (this._feedbackRevealTimer) {
        window.clearTimeout(this._feedbackRevealTimer);
        this._feedbackRevealTimer = null;
      }
      if (this._feedbackThanksTimer) {
        window.clearTimeout(this._feedbackThanksTimer);
        this._feedbackThanksTimer = null;
      }
      this.clearStreamReplyTimeout();
      this._chatReadyPromise = null;
      document.getElementById(MESSAGES_ID)?.replaceChildren();
      const field = composerField();
      if (field) {
        field.value = "";
        resizeComposerField(field, COMPOSER_MAX_HEIGHT_PX);
      }
      this.hasMessages = false;
      this.followUpSuggestionLabels = [];
      this._starterChipsShownTracked = false;
      this.trackStarterChipsShownOnce();
      this.emergencyHelpContactVisible = false;
      this.feedbackState = FEEDBACK_STATE_HIDDEN;
      this.replyInFlight = false;
      this._awaitingAssistantReply = false;
      this._sendPending = false;
      this._trackedRecommendationKeys = new Set();
      this._trackedGuardrailIds = new Set();
      const host = document.getElementById(CARD_HOST_ID);
      if (host) {
        host.dataset.replyInFlight = "false";
      }
    },

    async restoreExistingChat() {
      const fromQuery = consumeAskScanQuery(
        window.location.search,
        window.location.href
      );
      if (fromQuery.token && fromQuery.nextUrl) {
        window.history.replaceState({}, "", fromQuery.nextUrl);
        const resumed = await this.resumeChat(fromQuery.token);
        if (resumed) {
          this.openChat();
          return;
        }
      }

      const current = await this.fetchCurrentChat();
      if (current) {
        this.applyChatPayload(current);
        return;
      }

      const stored = readStoredResumeToken();
      if (stored) {
        await this.resumeChat(stored);
      }
    },

    async resumeChat(token) {
      try {
        let recaptchaToken = null;
        try {
          recaptchaToken = await fetchRecaptchaToken();
        } catch (_error) {
          return false;
        }

        if (recaptchaSiteKey() && !recaptchaToken) {
          return false;
        }

        const body = { resume_token: token };
        if (recaptchaToken) {
          body.recaptcha_token = recaptchaToken;
        }

        const response = await fetch("/chats/resume", {
          method: "POST",
          headers: jsonHeaders(csrfToken()),
          credentials: "same-origin",
          body: JSON.stringify(body),
        });
        if (response.status === 404) {
          writeStoredResumeToken(null);
          return false;
        }
        if (!response.ok) {
          return false;
        }
        const data = await response.json();
        this.applyChatPayload(data);
        return true;
      } catch (_error) {
        return false;
      }
    },

    async fetchCurrentChat() {
      try {
        const response = await fetch("/chats/current", {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        if (!response.ok) {
          return null;
        }
        const data = await response.json();
        if (!data?.chat_id) {
          return null;
        }
        return data;
      } catch (_error) {
        return null;
      }
    },

    applyChatPayload(data) {
      if (!data?.chat_id) {
        return;
      }
      this.chatId = data.chat_id;
      const panel = document.getElementById("ai-triage-panel");
      if (panel) {
        panel.dataset.aiTriageChatId = String(this.chatId);
      }
      this.resumeToken = data.resume_token || this.resumeToken;
      writeStoredResumeToken(this.resumeToken);
      this.chatBootstrapState = BOOTSTRAP_STATE_READY;
      this.chatBootstrapError = null;
      this.applyQuotaFromPayload({
        remainingMessages: data.remaining_messages,
        maxMessages: data.max_messages,
        retryAfterSeconds: data.retry_after_seconds,
      });
      this.emergencyHelpContactVisible =
        Boolean(data.escalated) && data.escalation_kind === GUARDRAIL_EMERGENCY;
      this.connectStream(data.signed_stream_name);
      if (data.feedback_submitted) {
        writeStoredFeedback(this.chatId, data.feedback_rating);
      }
      if (Array.isArray(data.messages) && data.messages.length > 0) {
        data.messages.forEach((message) => {
          if (message.recommendation?.productId) {
            this._trackedRecommendationKeys?.add(
              message.recommendation.productId
            );
          }
        });
        this.renderRestoredMessages(data.messages);
        if (data.messages.some((message) => message.recommendation)) {
          this.revealFeedbackPrompt();
        }
      }
    },

    renderRestoredMessages(messages) {
      const log = document.getElementById(MESSAGES_ID);
      if (!log || log.children.length > 0) {
        return;
      }
      messages.forEach((message) => {
        if (message.role === "user") {
          appendUserBubble(message.content);
          return;
        }
        const wrapper = document.createElement("div");
        wrapper.innerHTML = "";
        const row = document.createElement("div");
        row.id = `message_${message.id}`;
        row.className = "flex flex-row items-start gap-2.5";
        row.setAttribute("x-data", "aiChatMessage(false)");
        if (message.guardrail === GUARDRAIL_EMERGENCY) {
          row.dataset.aiTriageGuardrail = GUARDRAIL_EMERGENCY;
        }
        if (message.recommendation) {
          row.dataset.recommendation = JSON.stringify(message.recommendation);
        }
        const avatar = document.createElement("div");
        avatar.className =
          "flex h-7 w-7 shrink-0 items-center justify-center self-start rounded-full bg-green-lightest";
        const col = document.createElement("div");
        col.className = "flex min-w-0 max-w-[85%] flex-col items-start gap-2.5";
        const bubble = document.createElement("div");
        bubble.className =
          "min-w-0 rounded-2xl rounded-bl-md bg-neutral-100 px-4 py-3 text-neutral-900";
        const content = document.createElement("div");
        content.setAttribute("x-ref", "content");
        content.className =
          "ai-chat-stream break-words text-sm leading-relaxed";
        const source = document.createElement("div");
        source.setAttribute("x-ref", "streamSource");
        source.hidden = true;
        const raw = document.createElement("div");
        raw.id = `message_${message.id}_raw`;
        raw.setAttribute("x-ref", "raw");
        raw.hidden = true;
        raw.textContent = message.content || "";
        const settle = document.createElement("div");
        settle.id = `message_${message.id}_settle`;
        settle.setAttribute("x-ref", "settle");
        settle.hidden = true;
        source.appendChild(raw);
        source.appendChild(settle);
        bubble.appendChild(content);
        bubble.appendChild(source);
        const recommendation = document.createElement("div");
        recommendation.setAttribute("x-ref", "recommendation");
        recommendation.className = "w-full max-w-md";
        recommendation.hidden = true;
        col.appendChild(bubble);
        col.appendChild(recommendation);
        row.appendChild(avatar);
        row.appendChild(col);
        log.appendChild(row);
        if (window.Alpine) {
          window.Alpine.initTree(row);
        }
      });
      this.hasMessages = true;
    },

    ensureAiTriageChat() {
      if (this.chatId) {
        return Promise.resolve(this.chatId);
      }
      if (this._chatReadyPromise) {
        return this._chatReadyPromise;
      }
      this._chatReadyPromise = this.createChatAfterRestore();
      return this._chatReadyPromise;
    },

    async createChatAfterRestore() {
      if (this._restorePromise) {
        await this._restorePromise;
      }
      if (this.chatId) {
        return this.chatId;
      }
      return this.createChat();
    },

    async createChat() {
      this.chatBootstrapState = BOOTSTRAP_STATE_LOADING;
      this.chatBootstrapError = null;

      try {
        let recaptchaToken = null;
        try {
          recaptchaToken = await fetchRecaptchaToken();
        } catch (_error) {
          this.markBootstrapError(BOOTSTRAP_ERROR_VERIFY);
          return null;
        }

        if (recaptchaSiteKey() && !recaptchaToken) {
          this.markBootstrapError(BOOTSTRAP_ERROR_VERIFY);
          return null;
        }

        const body = {};
        if (recaptchaToken) {
          body.recaptcha_token = recaptchaToken;
        }

        const response = await fetch("/chats/create_ai_triage", {
          method: "POST",
          headers: jsonHeaders(csrfToken()),
          credentials: "same-origin",
          body: JSON.stringify(body),
        });

        if (response.status === 429) {
          const data = await response.json().catch(() => ({}));
          this.chatBootstrapState = BOOTSTRAP_STATE_ERROR;
          this.enterComposerBlock({
            reason: PAUSE_REASON_CHAT_CREATE,
            message: data.message || RATE_LIMIT_FALLBACK,
            retryAfterSeconds: Number(data.retry_after_seconds || 0),
          });
          trackAiTriageBlocked({
            reason: AI_TRIAGE_BLOCK_REASONS.RATE_LIMITED,
            stage: TRACK_STAGE_CREATE,
          });
          return null;
        }

        if (response.status === 422) {
          const data = await response.json().catch(() => ({}));
          this.markBootstrapError(
            data.message || BOOTSTRAP_ERROR_VERIFY,
            AI_TRIAGE_BLOCK_REASONS.RECAPTCHA
          );
          return null;
        }

        if (!response.ok) {
          throw new Error("Failed to create chat");
        }

        const data = await response.json();
        this.applyChatPayload(data);
        trackAiTriageChatCreated();
        return this.chatId;
      } catch (_error) {
        this.markBootstrapError(
          BOOTSTRAP_ERROR_GENERIC,
          AI_TRIAGE_BLOCK_REASONS.BOOTSTRAP
        );
        return null;
      } finally {
        if (!this.chatId) {
          this._chatReadyPromise = null;
        }
      }
    },

    markBootstrapError(message, reason = AI_TRIAGE_BLOCK_REASONS.RECAPTCHA) {
      this.chatBootstrapState = BOOTSTRAP_STATE_ERROR;
      this.chatBootstrapError = message;
      trackAiTriageBlocked({
        reason,
        stage: TRACK_STAGE_CREATE,
      });
    },

    connectStream(signedStreamName) {
      if (!signedStreamName) {
        return;
      }
      if (this._streamSource) {
        this.disconnectStream();
      }
      const streamSource = document.createElement("turbo-cable-stream-source");
      streamSource.setAttribute("channel", "Turbo::StreamsChannel");
      streamSource.setAttribute("signed-stream-name", signedStreamName);
      this._streamReady = waitForStreamSubscription(streamSource);
      document.body.appendChild(streamSource);
      this._streamSource = streamSource;
    },

    disconnectStream() {
      this._streamSource?.remove();
      this._streamSource = null;
      this._streamReady = null;
    },
  };
}

export function registerAiTriageHome(Alpine) {
  window.aiTriageHome = aiTriageHome;
  if (!Alpine || typeof Alpine.data !== "function") {
    return false;
  }
  registerAiChatLoader(Alpine);
  registerAiChatMessage(Alpine, scheduleTranscriptFollow);
  Alpine.data("aiTriageHome", aiTriageHome);
  return true;
}
